import type { DefaultObject } from '@/types'
import { deepClone } from '@/helpers/objects_arrays'
import { getVersionLevel } from '@/helpers/version'
import {
  migrateProjectDataParts,
  migrateProjectRoles,
  PROJECT_ROLE_OPTIONS,
  ROLE_PART_PREFIX,
} from '@/helpers/project-roles'
import type { UpdateDataType } from '@/components/partials/display/VersionUpdate.vue'

/**
 * Project state transfer helpers (sharing, duplication, restoring saved states).
 *
 * Project states (full project JSON) live in the CMS. A share file only carries
 * a reference (projectId + timestamp). Loading a state merges it into the
 * currently loaded project: project metadata (roles, parts, permissions) cannot
 * be changed from inside the widget, so only parts that exist in both projects
 * are overwritten and the rest is reported.
 */

export const PROJECT_STATE_REFERENCE_FORMAT = 'core-value-project-state'
export const PROJECT_STATE_REFERENCE_FORMAT_VERSION = 1

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export interface ProjectStateReference {
  projectId: string
  timestamp: number
  title: string | null
}

export interface ProjectStateReferenceFile extends ProjectStateReference {
  format: typeof PROJECT_STATE_REFERENCE_FORMAT
  formatVersion: number
  appVersion: string
}

export interface ProjectSourceMeta {
  title?: string
  roles?: string[]
  parts?: string[]
}

export interface ParsedProjectImport {
  project: DefaultObject
  meta: ProjectSourceMeta | null
  savedAt: number | null
}

export type ProjectReferenceParseError = 'invalid_json' | 'invalid_file'

export type ProjectImportIssueCode =
  | 'country_mismatch'
  | 'no_matching_roles'
  | 'incompatible_version'
  | 'roles_skipped'
  | 'roles_missing'
  | 'general_skipped_no_permission'
  | 'general_missing'

export interface ProjectImportIssue {
  code: ProjectImportIssueCode
  roles?: string[]
  parts?: string[]
  sourceCountry?: string | null
  currentCountry?: string | null
  sourceRoles?: string[]
  currentRoles?: string[]
}

export interface ProjectImportAnalysis {
  ok: boolean
  errors: ProjectImportIssue[]
  warnings: ProjectImportIssue[]
  /** The general part of the source will replace the current general part. */
  includesGeneral: boolean
  /** Roles whose setup/service parts will be overwritten. */
  importedRoles: string[]
  /** Roles present in the source but not editable in the current project. */
  skippedRoles: string[]
  /** Editable roles of the current project that the source does not contain; they are reset on load. */
  missingRoles: string[]
  /** Part keys whose major version differs from the current project; any entry blocks the load. */
  incompatibleParts: string[]
  /** Part keys that will actually be copied. */
  importedParts: string[]
}

export interface CurrentProjectContext {
  project: DefaultObject
  /** Roles the user may edit that also exist in the project (projectStore.getValidRoles). */
  editableRoles: string[]
  canEdit: boolean
}

export interface VersionUpdateClassification {
  /** Updates the loader applies automatically (patch and minor). */
  compatible: UpdateDataType[]
  /** Parts whose saved major version differs from the current parameters; loading is blocked. */
  incompatibleParts: string[]
}

const PROJECT_PART_SUFFIXES = ['setup', 'service'] as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isProjectPart(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && isRecord(value.data)
}

function getCountry(project: DefaultObject | null | undefined): string | null {
  const general = project?.general as Record<string, unknown> | undefined
  const data = general?.data as Record<string, unknown> | undefined
  return typeof data?.country === 'string' ? data.country : null
}

function majorVersion(version: unknown): number | null {
  if (typeof version !== 'string' || !version.length) return null
  const major = Number(version.split('.')[0])
  return Number.isFinite(major) ? major : null
}

function rolePartKeys(role: string): string[] {
  const prefix = ROLE_PART_PREFIX[role as keyof typeof ROLE_PART_PREFIX]
  if (!prefix) return []
  return PROJECT_PART_SUFFIXES.map((suffix) => `${prefix}_${suffix}`)
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

/** Roles the source project is about: its parts plus assigned roles (legacy names migrated). */
function declaredProjectRoles(meta: ProjectSourceMeta | null | undefined): string[] {
  return migrateProjectRoles([...stringArray(meta?.parts), ...stringArray(meta?.roles)])
}

/** Roles for which the project contains at least one setup or service part. */
export function getRolesInProject(project: DefaultObject): string[] {
  return PROJECT_ROLE_OPTIONS.filter((role) => rolePartKeys(role).some((part) => isProjectPart(project[part])))
}

export function buildProjectStateReference(
  state: { projectId: string; timestamp: number; label?: string | null },
  appVersion: string
): ProjectStateReferenceFile {
  return {
    format: PROJECT_STATE_REFERENCE_FORMAT,
    formatVersion: PROJECT_STATE_REFERENCE_FORMAT_VERSION,
    projectId: state.projectId,
    timestamp: state.timestamp,
    title: state.label?.trim() ? state.label.trim() : null,
    appVersion,
  }
}

export function buildReferenceFilename(title: string | undefined | null, date: Date = new Date()): string {
  const slug = (title ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '')
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `value-app-share_${slug || 'project'}_${year}-${month}-${day}.json`
}

export function parseProjectStateReference(
  text: string
):
  | { reference: ProjectStateReference; error?: undefined }
  | { reference?: undefined; error: ProjectReferenceParseError } {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    return { error: 'invalid_json' }
  }

  if (!isRecord(raw) || raw.format !== PROJECT_STATE_REFERENCE_FORMAT) {
    return { error: 'invalid_file' }
  }

  const projectId = raw.projectId
  const timestamp = raw.timestamp
  if (
    typeof projectId !== 'string' ||
    !UUID_PATTERN.test(projectId) ||
    typeof timestamp !== 'number' ||
    !Number.isInteger(timestamp) ||
    timestamp <= 0
  ) {
    return { error: 'invalid_file' }
  }

  return {
    reference: {
      projectId,
      timestamp,
      title: typeof raw.title === 'string' && raw.title.trim() ? raw.title.trim() : null,
    },
  }
}

/**
 * Normalizes a stored project state for loading: migrates legacy role parts
 * (ENGINEER, HVAC), drops malformed parts and the source's widgetMeta.
 */
export function prepareProjectSource(
  project: Record<string, unknown>,
  meta: Record<string, unknown> | null | undefined,
  savedAt: number | null = null
): ParsedProjectImport {
  const migrated = migrateProjectDataParts(deepClone(project)) as DefaultObject
  const cleaned: DefaultObject = {}
  for (const [key, value] of Object.entries(migrated)) {
    if (key === 'general' || key.endsWith('_setup') || key.endsWith('_service')) {
      if (isProjectPart(value)) cleaned[key] = value
      continue
    }
    if (key === 'widgetMeta') continue
    cleaned[key] = value
  }

  const sourceMeta: ProjectSourceMeta | null = isRecord(meta)
    ? {
        title: typeof meta.title === 'string' ? meta.title : undefined,
        roles: stringArray(meta.roles),
        parts: stringArray(meta.parts),
      }
    : null

  return { project: cleaned, meta: sourceMeta, savedAt }
}

export function analyzeProjectImport(
  source: ParsedProjectImport,
  current: CurrentProjectContext
): ProjectImportAnalysis {
  const errors: ProjectImportIssue[] = []
  const warnings: ProjectImportIssue[] = []

  const sourceCountry = getCountry(source.project)
  const currentCountry = getCountry(current.project)
  if (sourceCountry && currentCountry && sourceCountry !== currentCountry) {
    errors.push({ code: 'country_mismatch', sourceCountry, currentCountry })
  }

  // Prefer the roles declared by the source; default data contains empty parts
  // for every role, which should not count as importable roles.
  const rolesWithParts = getRolesInProject(source.project)
  const declaredRoles = declaredProjectRoles(source.meta)
  const sourceRoles = declaredRoles.length
    ? rolesWithParts.filter((role) => declaredRoles.includes(role))
    : rolesWithParts
  const editableRoles = current.editableRoles.filter((role) => PROJECT_ROLE_OPTIONS.includes(role as never))
  const importedRoles = sourceRoles.filter((role) => editableRoles.includes(role))
  const skippedRoles = sourceRoles.filter((role) => !editableRoles.includes(role))
  const missingRoles = editableRoles.filter((role) => !sourceRoles.includes(role))

  const sourceHasGeneral = isProjectPart(source.project.general)
  const includesGeneral = sourceHasGeneral && current.canEdit

  if (importedRoles.length === 0 && !(editableRoles.length === 0 && includesGeneral)) {
    errors.push({ code: 'no_matching_roles', sourceRoles, currentRoles: editableRoles })
  }

  if (skippedRoles.length) warnings.push({ code: 'roles_skipped', roles: skippedRoles })
  if (missingRoles.length) warnings.push({ code: 'roles_missing', roles: missingRoles })
  if (sourceHasGeneral && !current.canEdit) warnings.push({ code: 'general_skipped_no_permission' })
  if (!sourceHasGeneral && current.canEdit) warnings.push({ code: 'general_missing' })

  const candidateParts = [
    ...(includesGeneral ? ['general'] : []),
    ...importedRoles.flatMap((role) => rolePartKeys(role)).filter((part) => isProjectPart(source.project[part])),
  ]

  // The current project is at the current parameter versions (the version
  // guard enforces this), so a different major version in the saved state
  // means data that cannot be migrated: the whole load is blocked.
  const incompatibleParts = candidateParts.filter((part) => {
    const sourceMajor = majorVersion((source.project[part] as Record<string, unknown>).version)
    const currentMajor = majorVersion((current.project[part] as Record<string, unknown> | undefined)?.version)
    return sourceMajor !== null && currentMajor !== null && sourceMajor !== currentMajor
  })
  if (incompatibleParts.length) errors.push({ code: 'incompatible_version', parts: incompatibleParts })

  const ok = errors.length === 0
  return {
    ok,
    errors,
    warnings,
    includesGeneral: ok && includesGeneral,
    importedRoles: ok ? importedRoles : [],
    skippedRoles,
    missingRoles,
    incompatibleParts,
    importedParts: ok ? candidateParts : [],
  }
}

/**
 * Builds the project that replaces the current one. Editable roles the source
 * has no data for are removed so the server validation recreates them with
 * default (empty) data; widgetMeta (mirror id, language), lastDownloadedAt and
 * parts the user cannot edit stay untouched.
 */
export function buildMergedProject(
  source: ParsedProjectImport,
  current: CurrentProjectContext,
  analysis: ProjectImportAnalysis
): DefaultObject {
  const merged = deepClone(current.project) as DefaultObject

  for (const role of analysis.missingRoles) {
    for (const part of rolePartKeys(role)) {
      delete merged[part]
    }
  }

  for (const part of analysis.importedParts) {
    const sourcePart = deepClone(source.project[part]) as Record<string, unknown>
    const currentPart = current.project[part] as Record<string, unknown> | undefined
    if (typeof sourcePart.version !== 'string' || !sourcePart.version.length) {
      sourcePart.version = (currentPart?.version as string | undefined) ?? ''
    }
    merged[part] = sourcePart
  }

  let lastCalculatedAt = 0
  for (const value of Object.values(merged)) {
    const calculatedAt = (value as Record<string, unknown> | undefined)?.calculatedResults as
      | Record<string, unknown>
      | undefined
    const timestamp = calculatedAt?.calculatedAt
    if (typeof timestamp === 'number' && timestamp > lastCalculatedAt) lastCalculatedAt = timestamp
  }
  if (lastCalculatedAt) {
    merged.lastCalculatedAt = lastCalculatedAt
  } else {
    delete merged.lastCalculatedAt
  }

  return merged
}

/**
 * Splits the parameter updates reported by the server for a loaded project into
 * updates that can be applied automatically (patch, minor) and parts whose
 * major version is incompatible with the current parameters.
 */
export function classifyVersionUpdates(
  project: Record<string, unknown>,
  updates: UpdateDataType[]
): VersionUpdateClassification {
  const compatible: UpdateDataType[] = []
  const incompatibleParts: string[] = []

  for (const update of updates) {
    const part = project[update.part] as Record<string, unknown> | undefined
    const currentVersion = typeof part?.version === 'string' ? part.version : ''
    const level = currentVersion ? getVersionLevel(currentVersion, update.version) : null
    if (level === 'major') {
      incompatibleParts.push(update.part)
    } else if (level) {
      compatible.push(update)
    }
  }

  return { compatible, incompatibleParts }
}
